import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import type {
  LabResultCreateFromTemplatePayload,
  LabResultResponse,
  LabResultsResponse
} from '../types/lab_results.type'

type CreateLabResultPayload =
  | LabResultCreateFromTemplatePayload
  | {
      data: {
        type: 'lab_result'
        attributes: {
          attachment_signed_id: string
          test_name: string
        }
      }
    }

export interface UseLabResultsArgs {
  encounterId?: string
  patientId?: string
  query?: string
  limit?: string
  page?: string
  sortDir?: string
  sortBy?: string
  lockedDate?: string
  labTestNames?: string
  interpretation?: string
}

export const useLabResults = (arg: UseLabResultsArgs) => {
  const { encounterId, patientId, query, limit, page, sortDir, sortBy, lockedDate, labTestNames, interpretation } = arg

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `/lab_results?`
      if (patientId) endpoint += `patient_id=${patientId}&`
      if (encounterId) endpoint += `encounter_id=${encounterId}&`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (sortBy) endpoint += `sort_by=${sortBy}&`
      if (query) endpoint += `query=${query}&`
      if (limit) endpoint += `limit=${limit}&`
      if (interpretation) endpoint += `interpretation=${interpretation}&`
      if (labTestNames) endpoint += `lab_test_names[]=${labTestNames}`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (lockedDate) endpoint += `locked_date=${lockedDate}&`
      endpoint = endpoint.replace(/[&?]$/, '')
      const { data } = await AXIOS_INSTANCE.get<LabResultsResponse>(endpoint)
      return data
    },
    queryKey: [
      'lab_results',
      { encounterId, interpretation, limit, lockedDate, page, patientId, query, sortBy, sortDir }
    ]
  })

  return {
    included: data?.included,
    isLoading,
    labResults: data
  }
}

export const useLabResult = (orderId?: string, resultId?: string) => {
  // Get Lab Result
  const {
    data: labResultData,
    isLoading: isLabResultLoading,
    refetch: refetchLabResult
  } = useQuery<LabResultResponse | null>({
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return null
      const { data } = await AXIOS_INSTANCE.get<LabResultResponse>(`/lab_results/${resultId}`)
      return data
    },
    queryKey: ['lab_result', { resultId }]
  })

  // Create Lab Result
  const { mutateAsync: createLabResult } = useMutation({
    mutationFn: async ({ formVal, lab_order_id }: { formVal: CreateLabResultPayload; lab_order_id: string }) => {
      if (!lab_order_id) throw new Error('lab_order_id is required')
      const { data } = await AXIOS_INSTANCE.post(`/lab_orders/${lab_order_id}/lab_results`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { lab_order_id }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_result'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['lab_order', { lab_order_id }] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  // Update Lab Result
  const { mutateAsync: updateLabResult } = useMutation({
    mutationFn: async ({ formVal, resultId }: { formVal: any; resultId: string }) => {
      if (!resultId) throw new Error('resultId is required')
      const url = `/lab_results/${resultId}`
      const { data } = await AXIOS_INSTANCE.patch(url, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_data, { resultId: rid }) => {
      queryClient.invalidateQueries({ queryKey: ['lab_result', { resultId: rid }] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  // Delete Lab Result
  const { mutateAsync: deleteLabResult, isPending: isDeletingLabResult } = useMutation({
    mutationFn: async (resultId: string) => {
      if (!resultId) throw new Error('resultId is required')
      await AXIOS_INSTANCE.delete(`/lab_results/${resultId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_result'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['lab_order'] })
    }
  })

  return {
    createLabResult,
    deleteLabResult,
    isDeletingLabResult,
    isLabResultLoading,
    labResult: labResultData,
    refetchLabResult,
    updateLabResult
  }
}

export default useLabResult
